/**
 * Response shapes for the backend's `warehouse-reporting` endpoints.
 *
 * These come from the server already classified, and that is the point rather than an
 * accident. `qty` is a float, and the backend's tolerance is
 * `QTY_EPSILON + |largest qty in that location's ledger| * 2^-23` — scaled by the
 * location's throughput, so it cannot be reproduced in the browser without downloading the
 * whole ledger. Measured live it comes back around 6.05e-05, well above the flat 5e-05 the
 * UI's own rounding gives, so re-deriving "negative" here would paint rows red that the
 * ledger calls zero.
 *
 * So: read `isNegative`, `isZero` and `matchesLedger`. Never compare `levelQty` yourself.
 */

/**
 * Money, plus how much of the warehouse that money actually covers.
 *
 * Never render `value` on its own. `cost` is nullable — NULL means never priced, 0 means
 * deliberately priced at zero for 3PL stock the customer owns — so a total silently omits
 * every unit nobody has priced. On the dev data today `value` is 0 while
 * `unitsWithoutCostBasis` is 168, i.e. the figure covers none of the stock at all.
 */
export class StockValuation {
  /** Stock at cost. NOT an asset value — much of this warehouse belongs to customers. */
  value: number = 0;

  valuedUnits: number = 0;
  valuedProducts: number = 0;

  /** Units with no cost basis at all — nobody has priced them. */
  unitsWithoutCostBasis: number = 0;
  productsWithoutCostBasis: number = 0;

  /** Units deliberately priced at zero. A different fact from having no price. */
  unitsAtZeroCost: number = 0;
  productsAtZeroCost: number = 0;

  /**
   * Real stock, present on the shelf, whose weighted average cost is below zero — only a
   * write-down larger than the goods were ever worth produces this.
   *
   * Its own bucket because no other field catches it. `negativeUnits` keys off a negative
   * *quantity*, so a positive holding at a negative cost went through as valued and its
   * negative money cancelled healthy stock elsewhere: £1,000 of good stock beside a £1,000
   * over-write-down reported £0.00 for the whole warehouse, with every coverage field
   * agreeing the total was complete. Render it, or that case is invisible again.
   */
  unitsAtNegativeCost: number = 0;
  productsAtNegativeCost: number = 0;

  /** What those units would have taken off `value` had they been left in it. Negative. */
  negativeCostValueExcluded: number = 0;

  /** Negative-valued products over the whole ledger, not just the holdings shown here. */
  negativeValueProducts: number = 0;

  negativeUnits: number = 0;
  negativeValueExcluded: number = 0;

  /**
   * Of `negativeUnits`, the part sitting on products whose average is itself below zero — the ledger
   * is short AND the value has been written past nothing. A documented subset, not a fifth bucket:
   * these units are still counted in `negativeUnits`, so the two must never be added together.
   *
   * `negativeValueExcludedAtNegativeCost` is negative, like `negativeCostValueExcluded`: netting it
   * in would have increased the total rather than reduced it.
   */
  negativeUnitsAtNegativeCost: number = 0;
  negativeValueExcludedAtNegativeCost: number = 0;

  strandedValueAdjustment: number = 0;

  /**
   * Every unit actually present, and the denominator the buckets above divide up. Without it
   * a coverage figure cannot be read at all — 900 uncosted units is a rounding error in a
   * million and the entire warehouse in a thousand.
   */
  unitsOnHand: number = 0;

  /**
   * Units taken in on movements carrying no cost, over the products this report covers.
   *
   * NOT a migration artefact, and must not be worded as one: `product.service.ts` creates a
   * product with a null cost, so every new product starts here and stock booked in before
   * someone prices it lands here too. It is an ongoing condition, not a historic one.
   */
  unitsFromUncostedMovements: number = 0;
}

export class StockRow {
  productId: number = -1;
  productName: string = '';
  productDisplayName: string = null;
  sku: string = '';

  warehouseId: number = -1;
  warehouseName: string = '';
  warehouseLocationId: number = -1;
  aisle: string = '';
  shelf: string = '';
  locationLabel: string = '';

  /** What product_stock_level currently stores — a cache of the ledger. */
  levelQty: number = 0;

  /** Recomputed as SUM(signed qty) over the ledger. This is the one to display. */
  ledgerQty: number = 0;

  movements: number = 0;
  revaluations: number = 0;
  lastMovementAt: string = '';

  /** The scaled float tolerance this row was classified against. */
  tolerance: number = 0;

  isNegative: boolean = false;
  isZero: boolean = false;
  matchesLedger: boolean = true;
  hasLevel: boolean = true;

  unitCost: number = null;
  value: number = null;

  /**
   * Whether anything that put this stock here carried a unit cost. NOT whether the product has
   * a cost today — a product can be priced on the product screen and still have no cost basis,
   * because cost is snapshotted onto the movement at posting and never updated afterwards.
   */
  hasCostBasis: boolean = false;

  /** Has a cost basis AND that basis is not zero. */
  isPriced: boolean = false;

  /** 'uncosted' | 'priced' | 'zero-cost'. Read this rather than inferring from `unitCost`. */
  costState: string = 'uncosted';
}

export class DashboardSummary {
  valuation: StockValuation = new StockValuation();

  totalQty: number = 0;
  productsHoldingStock: number = 0;
  locationsInUse: number = 0;
  locationCount: number = 0;
  warehouseCount: number = 0;
  movementsToday: number = 0;
  movementsThisWeek: number = 0;
  negativeStockCount: number = 0;
  zeroStockCount: number = 0;

  /** Non-zero means the ledger and the stored levels disagree — a defect, not noise. */
  reconciliationDiscrepancies: number = 0;
}

export class StockOnHandResult {
  total: number = 0;
  totalQty: number = 0;
  valuation: StockValuation = new StockValuation();
  rows: StockRow[] = [];
}

export class AdjustmentTotals {
  movements: number = 0;
  increaseQty: number = 0;
  decreaseQty: number = 0;
  netQty: number = 0;

  increaseValue: number = 0;
  decreaseValue: number = 0;
  netValue: number = 0;

  uncostedMovements: number = 0;
  uncostedUnits: number = 0;
  productsWithoutCostBasis: number = 0;

  /**
   * Net weight over the WHOLE filtered set, not merely the rows returned — summed in SQL under
   * `weightCountedSql`, the same exclusion every weight aggregate in this app uses, so a
   * transfer's duplicate reading on its inbound row is already left out here. Null wherever
   * nothing in the set was weighed at both ends, never 0 — a 0 would say the goods were weighed
   * and had not changed. Never render this without `weighedMovements` / `unweighedMovements`
   * beside it: `SUM()` skips what it cannot add, so a net on its own says nothing about how much
   * of the set it covers.
   */
  netWeight: number = null;

  /** How many movements in the WHOLE set the net weight above is built from. */
  weighedMovements: number = 0;

  /**
   * How many movements the net weight does NOT cover. Present even when zero, for the same
   * reason `unbatchedQty` is: a figure that only appears when there is a problem cannot be told
   * from one somebody forgot to draw.
   */
  unweighedMovements: number = 0;
}

export class AdjustmentByReference {
  referenceId: number = -1;
  referenceCode: string = '';
  referenceName: string = '';
  movements: number = 0;
  netQty: number = 0;
  netValue: number = 0;
  uncostedMovements: number = 0;
}

export class MovementRow {
  id: number = -1;
  createdAt: string = '';
  movementType: string = '';

  qty: number = 0;

  /** What the movement does to the level, sign included. */
  signedQty: number = 0;

  productId: number = -1;
  productName: string = '';
  sku: string = '';

  warehouseId: number = -1;
  warehouseName: string = '';
  warehouseLocationId: number = -1;
  aisle: string = '';
  shelf: string = '';
  locationLabel: string = '';

  referenceId: number = -1;
  referenceCode: string = '';
  referenceName: string = '';
  clientRef: string = null;
  createdBy: number = -1;

  /**
   * What the goods weighed before and after this movement, and the net between them — or null
   * wherever a reading is missing, which is the normal case.
   *
   * Unlike `ProductStockMovement`, `netWeight` is a real field here rather than something the
   * screen derives: `getAdjustments` builds these rows through `WarehouseMovements.rows()`,
   * which already computes the bare `endWeight - startWeight` in SQL and lets NULL propagate
   * through it, so re-deriving it client-side would be a second computation of the same figure.
   * Null and never 0 for a row weighed once or not at all — a 0 would say the goods were weighed
   * twice and had not changed. Both sides of a transfer carry their own pair here; the duplicate
   * reading is only a problem in the totals above, not on a row.
   */
  startWeight: number = null;
  endWeight: number = null;
  netWeight: number = null;
}

export class AdjustmentsResult {
  rows: MovementRow[] = [];
  totals: AdjustmentTotals = new AdjustmentTotals();
  byReference: AdjustmentByReference[] = [];
}

/**
 * One (product, location) pair whose stored level disagrees with the ledger.
 *
 * The endpoint is inherently variance-only — it returns `checked` and `balanced` plus only
 * the rows that disagree, with the tolerance already applied. So a row here is a real
 * defect, never float noise.
 */
export class ReconciliationRow extends StockRow {
  /** levelQty - ledgerQty. */
  difference: number = 0;

  /** Which corruption case this is. */
  reason: string = '';
}

export class ReconciliationResult {
  checked: number = 0;
  balanced: boolean = true;
  discrepancies: ReconciliationRow[] = [];
}

/**
 * A holding with nothing moving it. `lastMovementAt` already excludes revaluations — money moving
 * with no stock moving must not reset the clock on a shelf nothing has touched — so a null here
 * means genuinely never moved, not merely "not moved since the last revaluation".
 */
export class SlowMovingRow extends StockRow {
  /** Null only in the defensive case where `isInUse` is true but no real movement was found. */
  daysSinceLastMovement: number = null;
}

export class SlowMovingResult {
  /** The threshold actually applied — echoed back so the screen can show what it asked for. */
  thresholdDays: number = 90;
  total: number = 0;
  rows: SlowMovingRow[] = [];
}

/**
 * One batch's balance at one location, or the un-batched remainder at that same location.
 *
 * A batch balance is replayed from the ledger and is **not enforced anywhere**. It keys no stock
 * level, gates no despatch and is not consulted by the insufficiency check, so it can be negative
 * and the report says so rather than clamping it.
 */
export class BatchBalance {
  /**
   * Null on the un-batched remainder, which is a bucket rather than a batch.
   *
   * There is no batch table, so this is the number itself and there is no id beside it. It cannot
   * refer to a batch that has been deleted either — a batch number exists exactly as long as a
   * movement carries it.
   */
  batchNumber: string = null;

  /** True on exactly the remainder rows. Never draw one of these as though it were a batch. */
  isUnbatched: boolean = false;

  productId: number = -1;
  productName: string = '';
  productDisplayName: string = '';
  sku: string = '';

  warehouseId: number = -1;
  warehouseName: string = '';
  warehouseLocationId: number = -1;
  aisle: string = '';
  shelf: string = '';
  locationLabel: string = '';

  qty: number = 0;

  /** Movements that moved stock. A revaluation is counted apart, below. */
  movements: number = 0;

  /** Money-only rows, which can only be non-zero on the remainder — a revaluation refuses a batch. */
  revaluations: number = 0;

  lastMovementAt: string = null;

  /** This group's own noise floor, from its own MAX(ABS(qty)). Never re-derive it here. */
  tolerance: number = 0;

  /** Server-classified against that tolerance, never against zero. Real negative stock. */
  isNegative: boolean = false;
  isZero: boolean = false;

  /**
   * What this batch (or, on a remainder row, this un-batched group) weighed, under the same
   * transfer-in exclusion every other weight total in this module uses. Null until something in
   * the group was weighed at both ends — never re-derive from the rows themselves, the backend
   * already replayed them under `weightCountedSql`.
   */
  netWeight: number = null;
  weighedMovements: number = 0;
  unweighedMovements: number = 0;
}

/**
 * What one product holds at one location, split into the numbered and un-numbered parts beside
 * their whole. This is the reconciliation the report exists to render.
 */
export class BatchLocationBalance {
  productId: number = -1;
  productName: string = '';
  sku: string = '';

  warehouseId: number = -1;
  warehouseName: string = '';
  warehouseLocationId: number = -1;
  locationLabel: string = '';

  /**
   * The whole balance at this product and location, taken from the same query pass.
   *
   * NOT `batchedQty + unbatchedQty`. Each part is rounded in its own right, so adding them back up
   * can differ by float noise, and a screen whose columns visibly fail to add up is exactly the
   * failure this figure exists to avoid.
   */
  ledgerQty: number = 0;

  /** The part of `ledgerQty` carrying a batch number. */
  batchedQty: number = 0;

  /**
   * The part carrying none — always reported, including when it is zero. This is stock that is
   * really there and really un-numbered, not stock unaccounted for.
   */
  unbatchedQty: number = 0;

  /** How many distinct batches contribute to `batchedQty`, whatever their balances. */
  batches: number = 0;

  tolerance: number = 0;
}

export class BatchBalancesResult {
  /** Batch rows only, and the only part of the payload the batch filters apply to. */
  rows: BatchBalance[] = [];

  /**
   * The un-batched remainder, its own explicit figure from `batchNumber IS NULL` on the same pass.
   *
   * Named `unbatchedDetail` to match the endpoint, which renamed it deliberately: an audit found that
   * the bare name `unbatched` on its own invited a screen to draw this as though it were the
   * authoritative remainder. It is not — `locations[].unbatchedQty` is. This is the per-row detail
   * behind that figure.
   *
   * Deliberately not filtered when a single batch is asked about: the remainder is not a batch, so
   * naming one cannot select or deselect it, and emptying it would say "there is no un-numbered
   * stock here" while `locations` went on reporting some for the same shelf.
   *
   * Legitimately empty on a fully-batched shelf. Empty does NOT mean zero un-batched stock.
   */
  unbatchedDetail: BatchBalance[] = [];

  /** Never batch-filtered, for the same reason. */
  locations: BatchLocationBalance[] = [];

  negativeBatches: number = 0;

  /**
   * Always false today, and read rather than assumed. The endpoint sends it so the screen has to
   * carry the statement that none of these figures is a control — a flag the frontend reads is
   * harder to leave out of the copy than a line in a document.
   */
  enforced: boolean = false;
}
